import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  InspectionStatus,
  InspectionAttempt,
  generateProtocolNumber,
  mapInspectionStatusToApartmentStatus,
} from '../types/inspection';
import { apartmentInspectionsApi, buildingsApi } from '../services/api';
import type { PickableHeatSource } from '../utils/heatSourceNames';
import { RecommendationsModal } from './RecommendationsModal';
import { attemptNumber, formatAttemptDate, romanTerm } from '../utils/attempts';
import { ModalShell } from '../ds/ModalShell';
import { InputField } from '../ds/InputField';
import { Button } from '../ds/components/Button';
import { Icon } from '../ds/components/Icon';
import { StatusChip } from '../ds/components/StatusChip';
import { StatusSegment, StatusSegmentOption } from '../ds/components/StatusSegment';
import { STATUS_LABELS } from '../ds/StatusMap';
import { ConfirmModal } from '../ds/ConfirmModal';
import {
  APARTMENT_FUNCTION_OPTIONS,
  DEFAULT_APARTMENT_FUNCTION,
  STATUSES_WITH_RECOMMENDATIONS,
  STATUS_TILE_LABELS,
  STATUS_TILE_ORDER,
  applyStatusChoice,
  inspectionDateError,
  localIsoDay,
  shouldAskToClearRecommendations,
} from '../utils/apartmentFormRules';
import { heatSourceSelectStyle } from './HeatSourceParamsFields';
import {
  expectedVersionOf,
  isRecordChangedError,
  recordDiffRows,
  ServerApartmentRecord,
} from '../utils/recordConflict';
import { RecordConflictModal } from './RecordConflictModal';
import { signatureStateLabel } from '../utils/authorizedSignature';
import { ApartmentHeatSourcesSection } from './ApartmentHeatSourcesSection';
import {
  ApartmentHeatState,
  heatDirtyAgainst,
  heatPayload,
  heatStateFromRecord,
  isDisplayableHeatSource,
  isHeatDirty,
} from '../utils/apartmentHeatSources';

/** Rekord przeglądu trzyma źródła lokalu jako JSONB z mobilki — bierzemy tylko to, czego potrzebuje picker. */
function toPickableHeatSources(raw: unknown): PickableHeatSource[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((hs): hs is Record<string, any> => !!hs && typeof hs === 'object' && typeof hs.type === 'string' && hs.type !== '')
    .map((hs, i) => ({
      id: hs.id != null && hs.id !== '' ? String(hs.id) : `apt-hs-${i}`,
      type: hs.type,
      heatCharacter: hs.heatCharacter ?? hs.heat_character,
      params: hs.params,
    }));
}

interface Props {
  apartmentId: string;
  apartmentNumber: string;
  buildingId: string;
  buildingAddress: string;
  inspectionId: string;
  onClose: () => void;
  onSave: () => void | Promise<void>;
}

/**
 * Status-segment options — pełny kanon 5 statusów jak na tablecie (zadanie 4, 2026-10-21: przegląd z papieru
 * wprowadzany w panelu; wcześniej panel znał tylko Nieobecność / Zalecenie / Brak uwag). StatusSegment reports the
 * option `id` (= InspectionStatus string sent to the API); `status` only drives the DS icon + color.
 * Etykiety i kolejność = kafle tabletu (utils/apartmentFormRules STATUS_TILE_LABELS — parytet:
 * backend/test/statusTileLabelsParity.test.js). Właściciel 2026-10-02: „Nieobecność (jak w tablecie)".
 */
const SEGMENT_STATUS: Record<(typeof STATUS_TILE_ORDER)[number], StatusSegmentOption['status']> = {
  brak_dostepu: 'noaccess',
  odmowa_lokatora: 'deny',
  zalecenie: 'rec',
  brak_uwag: 'ok',
  odmowa_wpisu: 'deny',
};
const STATUS_OPTIONS: StatusSegmentOption[] = STATUS_TILE_ORDER.map((id) => ({
  id,
  status: SEGMENT_STATUS[id],
  label: STATUS_TILE_LABELS[id],
}));

export const ApartmentInspectionForm: React.FC<Props> = ({
  apartmentId,
  apartmentNumber,
  buildingId,
  buildingAddress,
  inspectionId,
  onClose,
  onSave,
}) => {
  // Stan formularza
  const [protocolNumber, setProtocolNumber] = useState('');
  const [inspectionDate, setInspectionDate] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [inspectionStatus, setInspectionStatus] = useState<InspectionStatus>('');
  const [attempts, setAttempts] = useState<InspectionAttempt[]>([]);
  const [recommendations, setRecommendations] = useState('');
  // F9: stan podpisu lokatora z tabletu („Z upoważnienia" / podpis / brak) — tylko do wglądu; null = brak rekordu.
  const [signatureState, setSignatureState] = useState<string | null>(null);
  const [showRecommendationsModal, setShowRecommendationsModal] = useState(false);
  // Źródła ciepła LOKALU (Bronek 2026-10-12, kontrakt-API #416 pkt 3): stan edytowany w sekcji
  // „Źródła ciepła w lokalu" + stan wczytany z serwera. W zapisie idą WYŁĄCZNIE pola zmienione względem
  // wczytanych (heatPayload) — pominięte pole serwer zachowuje (COALESCE #57), więc formularz nie nadpisze
  // po cichu tego, co kominiarz zapisał na tablecie.
  const [heat, setHeat] = useState<ApartmentHeatState>(() => heatStateFromRecord(null));
  const [loadedHeat, setLoadedHeat] = useState<ApartmentHeatState>(() => heatStateFromRecord(null));
  // Rekordu tego przeglądu nie było przy odczycie (lista pokazuje źródła z ostatniego przeglądu lokalu).
  const [heatFromPreviousRecord, setHeatFromPreviousRecord] = useState(true);
  const heatChanged = isHeatDirty(heatDirtyAgainst(loadedHeat, heat));
  // Status wczytany z rekordu tego przeglądu ('' = rekordu nie ma) — patrz „Zapisz bez nowej próby".
  const [loadedStatus, setLoadedStatus] = useState<InspectionStatus>('');
  // Zadanie 4 (2026-10-21, przegląd z papieru): data kontroli = dzień wizyty z karty, nie dzień wprowadzenia w panelu
  // (z niej liczy się „Data rozpoczęcia" protokołu CEEB — utils/protocolDates — i termin zalecenia). `dateTouched` =
  // admin zmienił datę: wtedy jego wartość wygrywa także w „Zapisz moją wersję" po konflikcie.
  const [dateTouched, setDateTouched] = useState(false);
  // Funkcja lokalu (AIR.function_type → pole CEEB „Funkcja lokalu"): nowy rekord dostaje default tabletu; istniejący —
  // wysyłamy tylko zmienioną (pominięte pole serwer zachowuje, #57).
  const [functionType, setFunctionType] = useState<string>(DEFAULT_APARTMENT_FUNCTION);
  const [loadedFunctionType, setLoadedFunctionType] = useState<string | null>(null);
  // Ostatni zapis rekordu z panelu (`savedBy: 'Admin'`) albo rekordu jeszcze nie ma — sygnał „przegląd z papieru".
  const [enteredInPanel, setEnteredInPanel] = useState(true);
  // Źródła do „Nieszczelności”/„Ocena techniczna” (pkt 8/9, 2026-09-13) — LUSTRO mobilki
  // (InspectionFormScreen: `heatSources.length > 0 ? heatSources : buildingHeatSources`):
  // własne źródła lokalu (te z sekcji wyżej, także świeżo dodane), a dopiero gdy lokal
  // ich nie ma — wspólne źródła budynku (scope=building, dopisek „wspólne źródło budynku").
  const apartmentHeatSources = useMemo(
    () => toPickableHeatSources(heat.noHeatSource ? [] : heat.heatSources),
    [heat.noHeatSource, heat.heatSources],
  );
  const [buildingHeatSources, setBuildingHeatSources] = useState<PickableHeatSource[]>([]);
  const pickerHeatSources = apartmentHeatSources.length > 0 ? apartmentHeatSources : buildingHeatSources;
  const pickerHeatSourcesScope: 'apartment' | 'building' = apartmentHeatSources.length > 0 ? 'apartment' : 'building';
  useEffect(() => {
    let cancelled = false;
    buildingsApi
      .getById(buildingId)
      .then((b) => {
        if (!cancelled) setBuildingHeatSources((b.heatSources || []).map((hs) => ({ id: hs.id, type: hs.type, heatCharacter: hs.heatCharacter })));
      })
      .catch((error) => console.error('Nie udało się pobrać źródeł ciepła budynku:', error));
    return () => { cancelled = true; };
  }, [buildingId]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Błąd walidacji/zapisu widoczny W MODALU (baner role=alert) — zamiast alert(), który na
  // PWA/tablecie bywa wyciszony, a w panelu blokuje stronę (ZAKAZY CLAUDE.md).
  const [formError, setFormError] = useState<string | null>(null);
  // Baner stoi na górze przewijanego body modala — po kliknięciu „Zapisz" w stopce przy przewiniętym
  // formularzu byłby poza widokiem. Przewijamy do niego przy każdym nowym błędzie.
  const formErrorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (formError) formErrorRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [formError]);
  const [existingRecordId, setExistingRecordId] = useState<string | null>(null);
  // N1 (prompt 01/08, 2026-10-10): wersja rekordu, którą formularz wczytał — idzie w każdym zapisie jako
  // `expectedUpdatedAt` (jak z tabletu). Bez rekordu: NO_RECORD_VERSION (utils/recordConflict).
  const [loadedVersion, setLoadedVersion] = useState<string>(() => expectedVersionOf(null));
  // 409 RECORD_CHANGED: wersja z serwera + próby, z którymi poszedł odrzucony zapis („Zapisz nieobecność"
  // dokłada nową próbę — „Zapisz moją wersję" musi wysłać dokładnie to samo).
  const [conflict, setConflict] = useState<{ server: ServerApartmentRecord | null; attempts?: InspectionAttempt[] } | null>(null);
  // Data następnej próby dostępu (nowy element attempts). Default: dziś (YYYY-MM-DD).
  const [newAttemptDate, setNewAttemptDate] = useState<string>(
    () => new Date().toISOString().split('T')[0]
  );

  // ⚠️ 2026-07-28: numer terminu NIE jest już liczony tutaj. `termNumber` znaczy numer
  // TERMINU BUDYNKU, a ten wynika z daty wizyty i harmonogramu w `inspections` — stempluje go
  // backend (apartment-inspections.js → resolveTermNumber). Poprzedni licznik max+1 liczył
  // pukania do tych konkretnych drzwi i rozjeżdżał się z terminem, gdy lokal był pominięty.

  useEffect(() => {
    loadInspectionData();
  }, [inspectionId, apartmentId]);

  const loadInspectionData = async () => {
    try {
      setLoading(true);

      // Pobierz wszystkie przeglądy dla tego lokalu
      const records = await apartmentInspectionsApi.getByApartmentId(apartmentId);

      // Znajdź rekord dla tego inspection period
      const currentRecord = records.find(r => r.inspectionId === inspectionId);

      // Własne źródła lokalu: rekord bieżącego przeglądu, a gdy go jeszcze nie ma — ostatni
      // rekord lokalu (backend sortuje malejąco po dacie) — precedencja jak w paczce mobilki
      // (D8(a): ostatni przegląd). Pusta lista = fallback na wspólne źródła budynku.
      // Ta sama precedencja dla sekcji „Źródła ciepła w lokalu" (lista + flagi).
      const sourceRecord = currentRecord || records[0];
      const loaded = heatStateFromRecord(sourceRecord);
      setHeat(loaded);
      setLoadedHeat(loaded);
      setHeatFromPreviousRecord(!currentRecord);
      setLoadedStatus(currentRecord?.inspectionStatus || '');
      setLoadedVersion(expectedVersionOf(currentRecord));

      setDateTouched(false);
      if (currentRecord) {
        // Załaduj istniejące dane
        setExistingRecordId(currentRecord.id);
        const loadedFn = typeof currentRecord.functionType === 'string' && currentRecord.functionType ? currentRecord.functionType : null;
        setLoadedFunctionType(loadedFn);
        setFunctionType(loadedFn || '');
        setEnteredInPanel(currentRecord.savedBy === 'Admin');
        setProtocolNumber(currentRecord.protocolNumber);
        setInspectionDate(currentRecord.inspectionDate);
        setOwnerName(currentRecord.ownerName || '');
        setInspectionStatus(currentRecord.inspectionStatus || '');
        setAttempts(currentRecord.attempts || []);
        setRecommendations(currentRecord.recommendations || '');
        setSignatureState(signatureStateLabel(currentRecord));
      } else {
        // Nowy przegląd - wygeneruj dane
        // Ponowny odczyt („Wczytaj aktualną wersję") nie może zostawić w formularzu odrzuconych zmian.
        setExistingRecordId(null);
        setLoadedFunctionType(null);
        setFunctionType(DEFAULT_APARTMENT_FUNCTION);
        setEnteredInPanel(true);
        setOwnerName('');
        setInspectionStatus('');
        setAttempts([]);
        setRecommendations('');
        setSignatureState(null);
        setProtocolNumber(generateProtocolNumber(apartmentNumber));
        setInspectionDate(localIsoDay());
      }
    } catch (error) {
      console.error('❌ Błąd podczas ładowania danych przeglądu:', error);
      setFormError('Nie udało się wczytać danych lokalu z serwera. Sprawdź połączenie i spróbuj ponownie.');
      // Numer i datę generujemy tylko, gdy formularz ich jeszcze nie ma (nieudane „Wczytaj aktualną
      // wersję" nie podmienia wczytanych wcześniej). Wersja zostaje ta, którą formularz już ma
      // (na starcie NO_RECORD_VERSION) — jeśli rekord na serwerze jest nowszy, zapis dostanie 409.
      setProtocolNumber((current) => current || generateProtocolNumber(apartmentNumber));
      setInspectionDate((current) => current || new Date().toISOString().split('T')[0]);
    } finally {
      setLoading(false);
    }
  };

  // `expectedUpdatedAt`: wersja wczytana z formularzem; „Zapisz moją wersję" podaje wersję z odpowiedzi 409
  // i `server` — wtedy pola tylko-do-odczytu (numer protokołu, data przeglądu) idą z wersji z serwera:
  // formularz ich nie edytuje, a jego wartości bywają wygenerowane (rekordu nie było) albo nieaktualne.
  // Data musi zawsze pójść — bez niej zapis „zalecenia" wyzerowałby termin zalecenia (039).
  const saveInspection = async (
    updatedAttempts?: InspectionAttempt[],
    expectedUpdatedAt: string = loadedVersion,
    server?: ServerApartmentRecord | null,
  ) => {
    try {
      setSaving(true);

      const inspectionData = {
        inspectionId,
        apartmentId,
        buildingId,
        protocolNumber: server?.protocolNumber || protocolNumber,
        // Data zmieniona przez admina (przegląd z papieru) jedzie zawsze; nieruszona — wersja z serwera po konflikcie.
        inspectionDate: dateTouched ? inspectionDate : (server?.inspectionDate || inspectionDate),
        apartmentNumber,
        buildingAddress,
        ownerName,
        inspectionStatus,
        attempts: updatedAttempts || attempts,
        recommendations,
        savedBy: 'Admin' as const,
        expectedUpdatedAt,
        // Funkcja lokalu: nowy rekord — zawsze (jak tablet), istniejący — tylko zmieniona.
        ...(functionType && (!existingRecordId || functionType !== loadedFunctionType) ? { functionType } : {}),
        // Tylko zmienione pola źródeł ciepła; nieruszone = nieobecne w body (serwer zachowuje swoje).
        ...heatPayload(loadedHeat, heat, { newRecord: heatFromPreviousRecord }),
      };

      console.log('💾 [API] Zapisuję przegląd:', inspectionData);

      const savedRecord = await apartmentInspectionsApi.createOrUpdate(inspectionData);

      console.log('✅ [API] Przegląd zapisany:', savedRecord);
      setExistingRecordId(savedRecord.id);

      return savedRecord;
    } catch (error) {
      console.error('❌ [API] Błąd podczas zapisywania:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  };

  /**
   * Zapis + zamknięcie. 409 RECORD_CHANGED (rekord zmieniony na tablecie po otwarciu formularza) NIE jest
   * błędem połączenia: nic nie zapisano, okno konfliktu pyta, która wersja ma zostać.
   */
  const submit = async (
    updatedAttempts: InspectionAttempt[] | undefined,
    expectedUpdatedAt: string,
    server?: ServerApartmentRecord | null,
  ) => {
    try {
      await saveInspection(updatedAttempts, expectedUpdatedAt, server);
      if (updatedAttempts) setAttempts(updatedAttempts);
      setConflict(null);
      await onSave();
      onClose();
    } catch (error) {
      if (isRecordChangedError(error)) {
        setConflict({ server: error.record ?? null, attempts: updatedAttempts });
        return;
      }
      setConflict(null);
      setFormError(
        updatedAttempts
          ? 'Błąd podczas zapisywania nieobecności. Sprawdź połączenie z serwerem.'
          : 'Błąd podczas zapisywania przeglądu. Sprawdź połączenie z serwerem.',
      );
      console.error(error);
    }
  };

  // „Zapisz moją wersję" — jawne nadpisanie: ta sama treść, wersja (oraz numer i data) z odpowiedzi 409.
  const handleSaveMine = async () => {
    if (!conflict?.server?.updatedAt) return;
    await submit(conflict.attempts, conflict.server.updatedAt, conflict.server);
  };

  // „Wczytaj aktualną wersję" (po potwierdzeniu w oknie konfliktu) — zmiany z formularza odrzucone.
  const handleLoadServer = async () => {
    setConflict(null);
    setFormError(null);
    await loadInspectionData();
  };

  // „Brak uwag" / „Nieobecność" przy wpisanych zaleceniach (lustro tabletu, PR #341 — Bronek
  // 2026-10-02: „jest na zielono, ale zalecenia pozostały"): pytamy „Usunąć wpisane zalecenia?".
  // „Usuń zalecenia" = nowy status + puste zalecenia; „Anuluj" = poprzedni status i zalecenia.
  // Reguła: utils/apartmentFormRules (lustro mobilki).
  const [clearRecsFor, setClearRecsFor] = useState<InspectionStatus | null>(null);
  const applyStatus = (next: InspectionStatus, confirmed: boolean) => {
    const r = applyStatusChoice({ status: inspectionStatus, recommendations }, next, confirmed);
    setInspectionStatus(r.status);
    setRecommendations(r.recommendations);
    if (r.status === 'zalecenie' && next === 'zalecenie') setShowRecommendationsModal(true);
  };

  const handleStatusChange = (id: string) => {
    // StatusSegment reports the option id, which is the InspectionStatus string.
    const status = id as InspectionStatus;
    setFormError(null);
    // Także przy ponownym kliku w AKTYWNY status (jak na tablecie): rekord wczytany jako
    // „Brak uwag"/„Nieobecność" z zaleceniami — dokładnie przypadek Bronka — inaczej nie dałoby się
    // ich usunąć, a przy „Nieobecność" pole zaleceń jest ukryte i jechałyby niewidocznie.
    if (shouldAskToClearRecommendations(status, recommendations)) {
      setClearRecsFor(status);
      return;
    }
    applyStatus(status, false);
  };

  const handleSaveAttempt = async () => {
    if (inspectionStatus !== 'brak_dostepu') {
      setFormError(`Najpierw wybierz status „${STATUS_LABELS.brak_dostepu}”`);
      return;
    }
    setFormError(null);

    // Nowa próba w kształcie KANONICZNYM (021+): termNumber + status słownikowy +
    // data z pola „Data próby" (nie „zawsze dziś") + note. Bez legacy {attemptNumber,timestamp}.
    // Bez `termNumber` — serwer rozwiąże go z `date` względem terminów budynku.
    const newAttempt: InspectionAttempt = {
      status: inspectionStatus || 'brak_dostepu',
      date: newAttemptDate,
      note: null,
    };
    const updatedAttempts = [...attempts, newAttempt];

    // Sukces widać w liście lokali po odświeżeniu (status „Nieobecność" z datą) — bez alert().
    await submit(updatedAttempts, loadedVersion);
  };

  const handleSaveInspection = async () => {
    if (!inspectionStatus) {
      setFormError('Wybierz status kontroli lokalu');
      return;
    }

    if (inspectionStatus === 'brak_dostepu' && attempts.length === 0) {
      setFormError('Dodaj przynajmniej jedną próbę dostępu');
      return;
    }
    if (inspectionStatus !== 'brak_dostepu') {
      const dateErr = inspectionDateError(inspectionDate);
      if (dateErr) {
        setFormError(dateErr);
        return;
      }
    }
    setFormError(null);

    // Sukces widać w liście lokali po odświeżeniu — bez alert().
    await submit(undefined, loadedVersion);
  };

  if (loading) {
    return (
      <ModalShell
        open
        onClose={onClose}
        title={`Przegląd lokalu ${apartmentNumber}`}
        width={520}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 200,
            gap: 'var(--space-4)',
            color: 'var(--text-secondary)',
          }}
        >
          <Icon
            name="refresh"
            size={40}
            color="var(--action-primary)"
            style={{ animation: 'aif-spin 1s linear infinite' }}
          />
          <span style={{ fontSize: 'var(--text-body)' }}>Ładowanie danych...</span>
          <style>{`@keyframes aif-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </ModalShell>
    );
  }

  const readOnlyControlStyle: React.CSSProperties = {
    background: 'var(--surface-fill)',
    color: 'var(--text-disabled)',
    cursor: 'not-allowed',
  };

  return (
    <>
      <ModalShell
        open
        onClose={onClose}
        title={`Przegląd lokalu ${apartmentNumber}`}
        width={760}
        footer={
          <>
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              Anuluj
            </Button>

            {/* Zmienione źródła ciepła lokalu z „Nieobecnością": „Zapisz nieobecność" dokłada próbę dostępu,
                a admin może chcieć zapisać same źródła — wtedy zwykły zapis przeglądu (bez nowej próby).
                TYLKO gdy rekord JUŻ był „Nieobecnością": inaczej przycisk pozwalałby zmienić status na
                nieobecność bez próby dostępu (przy starszych próbach walidacja by to przepuściła). */}
            {loadedStatus === 'brak_dostepu' && inspectionStatus === 'brak_dostepu' && heatChanged && (
              <Button variant="secondary" onClick={handleSaveInspection} disabled={saving} icon="check">
                Zapisz bez nowej próby
              </Button>
            )}

            {inspectionStatus === 'brak_dostepu' ? (
              <Button
                variant="primary"
                onClick={handleSaveAttempt}
                disabled={saving}
                icon="plus"
              >
                {saving ? 'Zapisywanie...' : 'Zapisz nieobecność'}
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={handleSaveInspection}
                disabled={!inspectionStatus || saving}
                icon="check"
              >
                {saving ? 'Zapisywanie...' : 'Zapisz przegląd'}
              </Button>
            )}
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {formError && (
            <div
              ref={formErrorRef}
              role="alert"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-2) var(--space-3)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--status-deny-bg)',
                border: 'var(--border-width) solid var(--status-deny-bar)',
                color: 'var(--status-deny)',
                fontSize: 'var(--text-label)',
              }}
            >
              <Icon name="triangle-alert" size={18} />
              <span>{formError}</span>
            </div>
          )}
          {/* Numer protokołu */}
          <InputField
            label="Numer protokołu"
            value={protocolNumber}
            readOnly
            disabled
            style={readOnlyControlStyle}
          />

          {/* Adres */}
          <InputField
            label="Adres nieruchomości"
            value={buildingAddress}
            readOnly
            disabled
            style={readOnlyControlStyle}
          />

          {/* Data kontroli = dzień wizyty (lustro tabletu „Data kontroli", nie z przyszłości). Przy „Nieobecności"
              datę wizyty niesie „Data próby" niżej. Zadanie 4 (2026-10-21): przegląd z papieru wprowadzany po fakcie. */}
          {inspectionStatus === 'brak_dostepu' ? (
            <InputField
              label="Data kontroli"
              value={inspectionDate ? new Date(inspectionDate).toLocaleDateString('pl-PL') : ''}
              readOnly
              disabled
              style={readOnlyControlStyle}
            />
          ) : (
            <InputField
              label="Data kontroli"
              type="date"
              value={String(inspectionDate || '').slice(0, 10)}
              max={localIsoDay()}
              hint="Dzień wizyty w lokalu — przy przeglądzie z papieru wpisz datę z karty."
              onChange={(e) => { setDateTouched(true); setInspectionDate(e.target.value); }}
            />
          )}

          {/* F9: podpis lokatora z tabletu (tylko wgląd). Zadanie 4: lokal bez podpisu wprowadzony w panelu = „z papieru". */}
          <InputField
            label="Podpis lokatora"
            value={signatureState ?? 'Brak podpisu'}
            readOnly
            disabled
            style={readOnlyControlStyle}
          />
          {enteredInPanel && (signatureState === null || signatureState === 'Brak podpisu') && (
            <div
              data-testid="apartment-paper-entry"
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--space-2)',
                padding: 'var(--space-2) var(--space-3)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--surface-fill)',
                color: 'var(--text-secondary)',
                fontSize: 'var(--text-label)',
              }}
            >
              <Icon name="clipboard" size={18} />
              <span>
                Przegląd lokalu wprowadzony w panelu, bez podpisu lokatora na tablecie (np. z papierowej karty).
                Wygenerowana karta tego lokalu w załączniku CEEB będzie bez podpisu.
              </span>
            </div>
          )}

          {/* Funkcja lokalu (pole CEEB) — lustro tabletu. */}
          <div>
            <label
              htmlFor="apartment-function-type"
              style={{ display: 'block', marginBottom: 'var(--space-2)', fontSize: 'var(--text-label)', fontWeight: 'var(--weight-semibold)', color: 'var(--text-secondary)' }}
            >
              Funkcja lokalu
            </label>
            <select
              id="apartment-function-type"
              value={functionType}
              disabled={saving}
              onChange={(e) => setFunctionType(e.target.value)}
              style={heatSourceSelectStyle}
            >
              {functionType === '' && <option value="">— nie podano —</option>}
              {functionType !== '' && !APARTMENT_FUNCTION_OPTIONS.some((o) => o.value === functionType) && (
                <option value={functionType}>{functionType} (z CEEB)</option>
              )}
              {APARTMENT_FUNCTION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Oznaczenie specjalne lokalu */}
          <InputField
            label="Oznaczenie specjalne lokalu"
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
            placeholder="np. piwnica, garaż, lokal użytkowy"
          />

          {/* Źródła ciepła lokalu — przed statusem, jak na tablecie */}
          <ApartmentHeatSourcesSection
            value={heat}
            onChange={setHeat}
            dirty={heatChanged}
            fromPreviousInspection={heatFromPreviousRecord && loadedHeat.heatSources.some(isDisplayableHeatSource)}
            disabled={saving}
          />

          {/* Status kontroli lokalu — 5 segmentów jak kafle tabletu */}
          <div style={{ marginTop: 'var(--space-3)' }}>
            <div
              style={{
                fontSize: 'var(--text-label)',
                fontWeight: 'var(--weight-semibold)',
                color: 'var(--text-secondary)',
                marginBottom: 'var(--space-3)',
              }}
            >
              Status kontroli lokalu
            </div>
            <StatusSegment
              value={inspectionStatus || undefined}
              onChange={handleStatusChange}
              options={STATUS_OPTIONS}
            />
          </div>

          {/* Historia prób dostępu */}
          {inspectionStatus === 'brak_dostepu' && (
            <div style={{ marginTop: 'var(--space-3)' }}>
              {/* Data następnej próby (default dziś) — zapisywana w kanonicznym elemencie attempts */}
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <InputField
                  label="Data próby"
                  type="date"
                  value={newAttemptDate}
                  onChange={(e) => setNewAttemptDate(e.target.value)}
                />
              </div>
              <div
                style={{
                  fontSize: 'var(--text-label)',
                  fontWeight: 'var(--weight-semibold)',
                  color: 'var(--text-secondary)',
                  marginBottom: 'var(--space-3)',
                }}
              >
                Historia prób dostępu
              </div>
              {attempts.length > 0 ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    background: 'var(--surface-card)',
                    border: 'var(--border-width) solid var(--border)',
                    borderRadius: 'var(--radius-md)',
                    overflow: 'hidden',
                  }}
                >
                  {attempts.map((attempt, index) => {
                    // Etykieta terminowa (kanon 2026-07-28; „Próba N" = złamany parytet,
                    // naprawa 2026-08-04). Wzorzec 1:1 z historią w BuildingInspectionView.
                    const roman = romanTerm(attemptNumber(attempt, index));
                    return (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 'var(--space-4)',
                        padding: 'var(--space-3) var(--space-4)',
                        borderLeft: 'var(--bar-width) solid var(--status-deny-bar)',
                        borderBottom:
                          index < attempts.length - 1
                            ? 'var(--border-width) solid var(--border)'
                            : 'none',
                      }}
                    >
                      <span
                        style={{
                          fontSize: 'var(--text-body)',
                          fontWeight: 'var(--weight-medium)',
                          color: 'var(--text-primary)',
                          minWidth: 0,
                          overflowWrap: 'break-word',
                          wordBreak: 'break-word',
                        }}
                      >
                        {roman ? `${roman} termin` : 'Wizyta'}
                      </span>
                      <span
                        style={{
                          fontSize: 'var(--text-label)',
                          color: 'var(--text-secondary)',
                          minWidth: 0,
                          textAlign: 'right',
                          overflowWrap: 'break-word',
                          wordBreak: 'break-word',
                        }}
                      >
                        {formatAttemptDate(attempt)}
                      </span>
                    </div>
                    );
                  })}
                </div>
              ) : (
                <div
                  style={{
                    padding: 'var(--space-4)',
                    background: 'var(--surface-fill)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--text-body)',
                    color: 'var(--text-muted)',
                  }}
                >
                  Brak zapisanych prób
                </div>
              )}
            </div>
          )}

          {/* Zalecenia */}
          {STATUSES_WITH_RECOMMENDATIONS.includes(inspectionStatus) && (
            <div style={{ marginTop: 'var(--space-3)' }}>
              <div
                style={{
                  fontSize: 'var(--text-label)',
                  fontWeight: 'var(--weight-semibold)',
                  color: 'var(--text-secondary)',
                  marginBottom: 'var(--space-3)',
                }}
              >
                Zalecenia pokontrolne
              </div>
              {inspectionStatus === 'zalecenie' && (
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
                  <Button
                    variant="secondary"
                    onClick={() => setShowRecommendationsModal(true)}
                    icon="clipboard"
                  >
                    Wybierz z listy zaleceń
                  </Button>
                  {recommendations.trim().length > 0 && (
                    <StatusChip status="ok" label="Zalecenia zapisane" size="sm" />
                  )}
                </div>
              )}
              <InputField
                label="Uwagi i zalecenia"
                multiline
                rows={6}
                value={recommendations}
                onChange={(e) => setRecommendations(e.target.value)}
                placeholder="Uwagi i zalecenia..."
              />
            </div>
          )}
        </div>
      </ModalShell>

      <ConfirmModal
        open={clearRecsFor !== null}
        title="Usunąć wpisane zalecenia?"
        message={clearRecsFor
          ? `Lokal ma wpisane zalecenia. Status „${STATUS_LABELS[clearRecsFor] ?? clearRecsFor}” ich nie obejmuje — usunąć je z przeglądu tego lokalu?`
          : ''}
        confirmLabel="Usuń zalecenia"
        cancelLabel="Anuluj"
        destructive
        onConfirm={() => {
          const next = clearRecsFor;
          setClearRecsFor(null);
          if (next) applyStatus(next, true);
        }}
        onCancel={() => setClearRecsFor(null)}
      />

      <RecordConflictModal
        open={conflict !== null}
        apartmentNumber={apartmentNumber}
        server={conflict?.server ?? null}
        diff={conflict?.server
          ? recordDiffRows(conflict.server, {
            inspectionStatus,
            ownerName,
            recommendations,
            attempts: conflict.attempts ?? attempts,
            heat: heatPayload(loadedHeat, heat, { newRecord: heatFromPreviousRecord }),
          })
          : []}
        busy={saving}
        onLoadServer={handleLoadServer}
        onSaveMine={handleSaveMine}
        onClose={() => setConflict(null)}
      />

      {/* Modal zaleceń */}
      <RecommendationsModal
        visible={showRecommendationsModal}
        currentRecommendations={recommendations}
        heatSources={pickerHeatSources}
        heatSourcesScope={pickerHeatSourcesScope}
        onSave={(text) => {
          setRecommendations(text);
          setShowRecommendationsModal(false);
        }}
        onCancel={() => setShowRecommendationsModal(false)}
      />
    </>
  );
};
